import type { TimerSyncState } from "@common/use-cases/timer/timer-sync";
import EventEmitter from "eventemitter3";
import { makeAutoObservable, runInAction } from "mobx";

export type TimerState = "PAUSED" | "RUNNING" | "STOPPED" | "FINISHED";
export class Timer {
  totalDuration = 0; // ms
  timeStart: number | null = null; // unix
  timePaused: number | null = null; // unix
  timerState: TimerState = "STOPPED";

  _clock = 0;
  _clockTimer: ReturnType<typeof setInterval> | null = null;

  private startClock() {
    if (this._clockTimer) clearInterval(this._clockTimer);
    this._clockTimer = setInterval(() => {
      runInAction(() => {
        this._clock++;
      });

      this.events.emit("ON_TIME_UPDATED", this);
      if (this.getTimeLeft() <= 0) {
        this.finish();
      }
    }, 250);
  }

  private stopClock() {
    if (this._clockTimer) clearInterval(this._clockTimer);
  }

  events = new EventEmitter<{
    ON_STATE_CHANGED: (timerState: TimerState) => void;
    ON_TIME_UPDATED: (timer: Timer) => void;
    ON_TIMER_COMPLETED: (timer: Timer) => void;
  }>();

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * @param duration how long the timer should be in millseconds
   */
  start(duration: number) {
    this.totalDuration = duration;
    if (this.timerState !== "STOPPED") throw new Error("Timer already running");

    this.timeStart = Date.now();
    this.timerState = "RUNNING";

    this.startClock();
  }

  unpause() {
    if (!["PAUSED", "FINISHED"].includes(this.timerState))
      throw new Error("Timer not paused");
    if (!this.timeStart) throw new Error("Timer not started");
    const timePausedAt = this.timePaused ?? 0;
    const timeProgressed = timePausedAt - this.timeStart;
    this.timeStart = Date.now() - timeProgressed;
    this.timerState = "RUNNING";

    this.startClock();
  }

  extend(duration: number) {
    this.totalDuration += duration;
  }

  stop() {
    this.timerState = "STOPPED";
    this.timeStart = null;
    this.timePaused = null;
    this.totalDuration = 0;

    this.stopClock();
  }

  finish() {
    this.pause();
    this.timerState = "FINISHED";

    this.stopClock();
    this.events.emit("ON_TIMER_COMPLETED", this);
  }

  pause() {
    this.timerState = "PAUSED";
    this.timePaused = Date.now();

    this.stopClock();
  }

  getTimeLeft(): number {
    return this.totalDuration - this.getTimePast();
  }

  getTimePast(): number {
    if (this.timerState === "PAUSED" && this.timePaused && this.timeStart)
      return this.timePaused - this.timeStart;
    if (this.timerState === "RUNNING" && this.timeStart)
      return (
        Math.round(
          Math.min(Date.now() - this.timeStart, this.totalDuration) / 1000
        ) * 1000
      ); // to nearest second
    return 0;
  }

  getProgress(): number {
    return this.getTimePast() / this.totalDuration;
  }

  public syncToState(syncPayload: TimerSyncState) {
    this.totalDuration = syncPayload.totalDuration;
    this.timeStart = syncPayload.timeStart;
    this.timePaused = syncPayload.timePaused;
    this.timerState = syncPayload.timerState;

    switch (this.timerState) {
      case "RUNNING":
        this.startClock();
        return;
      case "PAUSED":
      case "STOPPED":
      case "FINISHED":
        this.stopClock();
        return;
    }
  }

  public dispose() {
    this.stop();
  }
}
